import {
  ContentType,
  type AdSlot,
  type LoadRequest,
  type ManifestConfiguration,
  type SmartclipPluginConfig,
} from '@foundation-player/api';
import type { VideoEncoding, VideoProps, VideoTags } from 'types/videoTypes';
import { ensurePath, getVpShare } from 'utils/ads';
import { getVideoDuration } from 'utils/videoPlayer';
import { getSeoTitle } from '../SchemaObjects/helper/utils';

export const FOUNDATION_CLIENT_ID_DEFAULT = 'html5/rtldigitalnews/rtl';
export const FOUNDATION_CLIENT_ID_LONG = 'html5/rtldigitalnews/rtl/rtlplusmedia';

export const defaultErrorMessage = 'Dieses Video ist leider nicht verfügbar.';
export const geoBlockingErrorMessage = 'Dieses Video ist in deinem Land leider nicht verfügbar.';
export const dateErrorMessage = 'Dieses Video ist leider nicht mehr verfügbar.';

export type VideoEmbedType = 'inline' | 'inline_related' | 'intro' | 'playlist';

type CreateLoadRequestProps = {
  video: VideoProps;
  tcString: string;
  ivwCode: string;
  env?: string;
  adsDisabled?: boolean;
  postRollDisabled?: boolean;
  sxpKeys?: string;
  contentType?: ContentType;
  autoplay?: boolean;
  posterUrl?: string;
  startType?: number;
  version?: string;
  device?: string;
  clientIdentifier?: string;
  moltenBundleSmartclipConfig?: SmartclipPluginConfig['smartclipConfig'];
  embedType?: VideoEmbedType;
};

export type SlotType = 'pre' | 'mid' | 'post';

export const envDecider = (env?: string) => {
  switch (env) {
    case 'preview':
    case 'test':
    case 'preprod':
    case 'dev':
      return 'preprod';
    case 'prod':
    default:
      return 'prod';
  }
};

export const createLoadRequest = ({
  video,
  tcString,
  ivwCode,
  env,
  adsDisabled,
  postRollDisabled,
  sxpKeys,
  contentType = ContentType.Vod,
  autoplay = false,
  posterUrl,
  startType = 0,
  version = '0.0.1',
  device,
  clientIdentifier = FOUNDATION_CLIENT_ID_DEFAULT,
  moltenBundleSmartclipConfig,
  embedType,
}: CreateLoadRequestProps): LoadRequest => {
  const videoDuration = getVideoDuration(video);
  const isLongform: boolean = clientIdentifier === FOUNDATION_CLIENT_ID_LONG;
  const videoTags = video.tags?.map((item: VideoTags) => item.name);
  const adSlots: Array<AdSlot> = [];
  let manifests: Array<ManifestConfiguration> | undefined = undefined;
  const smartclipConfig: SmartclipPluginConfig['smartclipConfig'] =
    typeof moltenBundleSmartclipConfig === 'object' && moltenBundleSmartclipConfig !== null
      ? moltenBundleSmartclipConfig
      : {};
  ensurePath(smartclipConfig, ['adReinsertion', 'homad', 'setup']).clientConfig =
    'https://cdn.static-fra.de/now/sc/rtl.de.clientConfig.json';

  function createAdCall(slot: SlotType): string {
    const paramsObj: Record<string, string> = {
      type: 'vast4',
      fwd_adb: '0',
      s: getVpShare(ivwCode),
      sz: slot,
      tagver: '6.1.1',
      fwd_comp: '0',
      ang_cntp: 'none',
      ang_streamid: crypto.getRandomValues(new Uint32Array(1)).toString(),
      fwd_conid: video.sourceId,
      rnd: crypto.getRandomValues(new Uint32Array(1)).toString(),
      ang_ref: encodeURI(window?.location?.href ?? 'https://www.rtl.de'),
      plcmt: '1',
    };

    if (videoDuration && Math.round(videoDuration) > 0) {
      paramsObj.ang_conlen = Math.round(videoDuration).toString();
    }

    const params = new URLSearchParams(paramsObj).toString();

    return `https://ad-ipd.sxp.smartclip.net/select?${params}&${sxpKeys}&consent=${tcString}`;
  }

  if (!adsDisabled) {
    adSlots.push({
      offsetInSeconds: 0,
      type: 'linear',
      adCall: createAdCall('pre'),
    });
  }

  if (videoDuration && videoDuration > 480 && !adsDisabled && !isLongform) {
    adSlots.push({
      offsetInSeconds: 180,
      type: 'linear',
      adCall: createAdCall('mid'),
    });
  }

  if (videoDuration && videoDuration > 30 && !adsDisabled && !postRollDisabled) {
    adSlots.push({
      offsetInSeconds: -1,
      type: 'linear',
      adCall: createAdCall('post'),
    });
  }

  if (video.fields?.contributor) {
    videoTags?.push(video.fields.contributor);
  }

  if (contentType === 'fast' && video?.fields?.encoding) {
    manifests = video.fields.encoding.map(
      (enc: VideoEncoding) =>
        ({
          type: enc.value['encoding-format'].value,
          sources: [{ url: enc.value['content-url'].value }],
        }) as ManifestConfiguration
    );
  }

  return {
    contentType: contentType,
    contentId: video.sourceId,
    user: {
      consentString: tcString,
    },
    remoteConfig: {
      clientIdentifier: clientIdentifier,
      environment: envDecider(env),
    },
    autoplay: autoplay,
    contentMetadata: {
      title: decodeURIComponent(video.fields.title),
      headline: decodeURIComponent(video.fields.title),
      secondaryHeadline: decodeURIComponent(video.fields?.subtitle ?? ''),
      firstPublicationDate: video.createdAt ?? '',
      lastPublicationDate: video.lastmodifiedAt ?? '',
      posterUrl: posterUrl,
      durationInSeconds: videoDuration,
    },
    plugins: {
      'foundation.plugin.smartclip': {
        enabled: !adsDisabled,
        adSlots: adSlots,
        smartclipConfig: smartclipConfig,
      },
      'foundation.plugin.googleAnalytics4': {
        enabled: true,
        progressTrackingIntervalInSeconds: 15,
        headline: getSeoTitle(video.fields.title, video.fields.subtitle),
        firstHeadline: getSeoTitle(
          video.fields['first-title'] ?? video.fields.title,
          video.fields['first-subtitle'] ?? video.fields.subtitle
        ),
        tags: videoTags,
        editorialUnit: video.fields.producer ?? '',
        contentType: 'video',
        embedType: embedType,
      },
      'foundation.plugin.heartbeat': {
        startType: startType,
        offer: device === 'ios' ? 'apprtlin' : device === 'android' ? 'aadrtlin' : 'rtl',
        ivw: ivwCode,
      },
      'foundation.plugin.npaw': {
        appVersion: version,
        userType: 'free',
        channelName: 'rtl',
      },
    },
    manifests: manifests,
  };
};
